import flow from 'lodash/fp/flow';
import keys from 'lodash/fp/keys';
import pickBy from 'lodash/fp/pickBy';
import get from 'lodash/get';
import some from 'lodash/some';

import {FORM_FIELDS} from '@components/Person/PersonOptionsSelection/MapPeopleFilter/PersonLocationTypesSelector/constants';

import {FEATURES} from '@constants/features';
import {LAYERS} from '@constants/mapbox/mapbox';

import {featureToggle} from '@utils/featureToggle/featureToggle';

import {DEFAULT_PARENT_ID} from '../../../../components/Organizations/constants';
import {
  ACCESS_POINT,
  ADDRESS,
  AIRLINE_ID,
  AIRLINE_IDS,
  AIRPORT_ID,
  ALL,
  EXPATRIATION,
  FLIGHT_NUMBER_LIKE,
  FLIGHT_NUMBERS,
  INCLUDE_SUB_ORGS,
  LAST,
  MAP_MOBILE_LOCATIONS,
  MAP_PEOPLE,
  MAP_TRIPS,
  OCCUPANT,
  PERSON_ORGS,
  POC,
  RAILWAY,
  SEPARATE_MOBILE_LOCATIONS,
  TRAIN_NUMBER,
} from '../../../../components/Person/PersonOptionsSelection/form-constants';
import {
  VIEW_ACCESS_POINTS,
  VIEW_CRISIS_SIGNAL_LOCATIONS,
  VIEW_EXPATRIATIONS_FILTER,
  VIEW_MOBILE_LOCATION_ON_MAP,
  VIEW_PERSONS,
  VIEW_PERSONS_GEOCODED_ADDRESS,
  VIEW_PERSONS_MOBILE_LOCATION,
  VIEW_SAFETY_CHECKIN_SIGNAL_LOCATIONS,
  VIEW_TRIPS_FILTER,
} from '../../../../constants/permissions';
import {WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE} from '../../../../constants/person-filters';
import {constants, parseFilterParams} from '../../persons/utils';
import {filterOrgsPerAvailablePersonas, getAggregatedPermissionIdsObject} from '../_shared/utils';

const imageCDN = `${process.env.REACT_APP_CDN_URL}/images/world-map`;

export const EMPTY_PERSON_CLUSTER_ID = '___EMPTY___PERSON___CLUSTER___';

export const PERSON_IMAGE_SRC = {
  CRISIS_SIGNAL: `${imageCDN}/marker_crisis.svg`,
  SAFETY_CHECK_IN: `${imageCDN}/marker_checkin.svg`,
  MOBILE_BACKGROUND_SHARE: `${imageCDN}/marker_person.svg`,
  TRAIN: `${imageCDN}/icon_pin_rail.svg`,
  PLANE: `${imageCDN}/icon_pin_flight_white.svg`,
  HOTEL: `${imageCDN}/icon_pin_hotel_white.svg`,
  CAR: `${imageCDN}/icon_pin_car.svg`,
  ADDRESSES: `${imageCDN}/icon_pin_address_white.svg`,
  EXPATRIATIONS: `${imageCDN}/icon_pin_expatriation.svg`,
  ACCESS_POINTS: `${imageCDN}/icon_pin_access_point.svg`,
};

export const PERSON_LOCATION_TYPES_EXCLUDED_VALUES = {
  MAP_PEOPLE: 'MAP_PEOPLE',
  MAP_MOBILE_LOCATIONS: 'MAP_MOBILE_LOCATIONS',
  MAP_TRIPS: 'MAP_TRIPS',
};

export const PERSON_MOBILE_LOCATIONS_VALUES = {
  CRISIS_SIGNAL: 'CRISIS_SIGNAL',
  SAFETY_CHECK_IN: 'SAFETY_CHECK_IN',
  MOBILE_BACKGROUND_SHARE: 'MOBILE_BACKGROUND_SHARE',
};

export const PERSON_TRIPS_VALUES = {
  TRIP_AIR: 'TRIP_AIR',
  TRIP_HOTEL: 'TRIP_HOTEL',
  TRIP_RAIL: 'TRIP_RAIL',
  TRIP_CAR: 'TRIP_CAR',
};

export const PERSON_SITE_VALUES = {
  OCCUPANT: OCCUPANT,
  POC: POC,
};

/**
 *  The required permissions for all of the VIEW_PERSONS types
 *  was changed to VIEW_PERSONS in this Jira issue:
 *  https://gardaworld.atlassian.net/browse/HORIZONWEB-2850
 *  */
export const PERSON_MAPPING = [
  {permissions: [VIEW_PERSONS], value: PERSON_MOBILE_LOCATIONS_VALUES.CRISIS_SIGNAL},
  {permissions: [VIEW_PERSONS], value: PERSON_MOBILE_LOCATIONS_VALUES.SAFETY_CHECK_IN},
  {permissions: [VIEW_PERSONS], value: PERSON_MOBILE_LOCATIONS_VALUES.MOBILE_BACKGROUND_SHARE},
  // trips
  {permissions: [VIEW_PERSONS], value: PERSON_TRIPS_VALUES.TRIP_AIR},
  {permissions: [VIEW_PERSONS], value: PERSON_TRIPS_VALUES.TRIP_HOTEL},
  {permissions: [VIEW_PERSONS], value: PERSON_TRIPS_VALUES.TRIP_RAIL},
  {permissions: [VIEW_PERSONS], value: PERSON_TRIPS_VALUES.TRIP_CAR},
];

const defaultRootOrgArray = [DEFAULT_PARENT_ID];

export function getOrgsWithViewPersonsPermission(fullPermissions) {
  return flow([pickBy((value) => value?.permissions?.[VIEW_PERSONS]), keys])(fullPermissions);
}

export function getMapSelectedPeopleOrgsPerFilter(personOrgsFilter, fullPermissions) {
  if (personOrgsFilter?.length > 0) {
    return personOrgsFilter;
  }

  const defaultOrgsFilter = getOrgsWithViewPersonsPermission(fullPermissions);
  if (defaultOrgsFilter?.includes(DEFAULT_PARENT_ID)) {
    return defaultRootOrgArray;
  }

  return defaultOrgsFilter;
}

export function parsePersonPolygonFeatureResult(response, filterParams) {
  const idCount = get(response, ['data', 'totalElements'], 0);
  const singlePersonId = get(response, ['data', 'content', 0, 'id']);

  if (idCount > 0) {
    return {
      layer: {
        id: LAYERS.PERSON_LAYER_ID,
      },
      properties: {
        id: idCount === 1 && singlePersonId,
        idCount,
      },
      personFilterParams: filterParams,
      filterParams,
      isPolygonSelection: true,
    };
  }

  return []; //empty set for no results
}

export function filterPersonViaPermissions(hasPermission = {}, personTypes) {
  return PERSON_MAPPING.reduce((accumulator, item) => {
    const acceptedPermissions = item.permissions.filter((permission) => {
      return hasPermission[permission];
    });

    if (personTypes?.includes?.(item.value) && acceptedPermissions.length === item.permissions.length) {
      accumulator.push(item.value);
    }

    return accumulator;
  }, []);
}

/**
 * Returns true if PERSON_LAYER_ID
 *
 * @param feature
 * @returns {*}
 */
export function isPersonFeature(feature) {
  const layerId = get(feature, 'layer.id');

  return [
    LAYERS.PERSON_LAYER_ID,
    LAYERS.CRISIS_SIGNAL_LAYER_ID,
    LAYERS.PERSON_DETAIL_LAYER_ID,
    LAYERS.NON_MAP_AFFECTED_PEOPLE_LAYER_ID,
    LAYERS.NON_MAP_AFFECTED_PEOPLE_AT_SITES_LAYER_ID,
  ].includes(layerId);
}

export function isSitesFeature(feature) {
  const layerId = get(feature, 'layer.id');
  return layerId === LAYERS.NON_MAP_AFFECTED_SITES_LAYER_ID || layerId === LAYERS.SITE_LAYER_ID;
}

function getPersonGroupData(
  localStorageMapPeople,
  {hasViewMobileLocationOnMap, viewTripsFilter, viewExpatriationsFilter, viewAddress, viewAccessPoints},
) {
  let itemsToRemove = [];

  if (!hasViewMobileLocationOnMap) {
    itemsToRemove = [
      ...itemsToRemove,
      MAP_MOBILE_LOCATIONS,
      PERSON_MOBILE_LOCATIONS_VALUES.CRISIS_SIGNAL,
      PERSON_MOBILE_LOCATIONS_VALUES.SAFETY_CHECK_IN,
      PERSON_MOBILE_LOCATIONS_VALUES.MOBILE_BACKGROUND_SHARE,
    ];
  }

  if (!viewTripsFilter) {
    itemsToRemove = [
      ...itemsToRemove,
      MAP_TRIPS,
      PERSON_TRIPS_VALUES.TRIP_AIR,
      PERSON_TRIPS_VALUES.TRIP_CAR,
      PERSON_TRIPS_VALUES.TRIP_RAIL,
      PERSON_TRIPS_VALUES.TRIP_HOTEL,
    ];
  }

  if (!viewExpatriationsFilter) {
    itemsToRemove = [...itemsToRemove, EXPATRIATION];
  }

  if (!viewAccessPoints) {
    itemsToRemove = [...itemsToRemove, ACCESS_POINT];
  }

  if (!viewAddress) {
    itemsToRemove = [...itemsToRemove, ADDRESS];
  }

  itemsToRemove = [...itemsToRemove, MAP_PEOPLE];

  return localStorageMapPeople?.[FORM_FIELDS.PERSON_LOCATION_TYPES]?.filter((item) => !itemsToRemove.includes(item));
}

export function getLocalStorageMapPeople(state, worldMapLocalStorage) {
  let localStorageMapPeople = get(worldMapLocalStorage, [WORLD_MAP_FILTER_PERSON_STORAGE_KEY_BASE]);
  const verifiedPeopleOrgs = filterOrgsPerAvailablePersonas(state, localStorageMapPeople?.[PERSON_ORGS]);
  const hasPersonPermission = getAggregatedPermissionIdsObject(state);
  const hasViewPerson = hasPersonPermission?.[VIEW_PERSONS];

  let personSubPermissions = [
    VIEW_SAFETY_CHECKIN_SIGNAL_LOCATIONS,
    VIEW_PERSONS_MOBILE_LOCATION,
    VIEW_PERSONS_GEOCODED_ADDRESS,
  ];

  const tripPermission = VIEW_TRIPS_FILTER;
  const expatPermission = VIEW_EXPATRIATIONS_FILTER;
  personSubPermissions.push(tripPermission);
  personSubPermissions.push(expatPermission);

  const hasAnyOfPersonSubPermissions = some(personSubPermissions, (permission) => hasPersonPermission?.[permission]);

  const hasViewMobileLocationOnMap = hasPersonPermission?.[VIEW_MOBILE_LOCATION_ON_MAP];

  const personGroupData = getPersonGroupData(localStorageMapPeople, {
    hasViewMobileLocationOnMap,
    viewTripsFilter: hasPersonPermission?.[tripPermission],
    viewExpatriationsFilter: hasPersonPermission?.[expatPermission],
    viewAddress: hasPersonPermission?.[VIEW_PERSONS_GEOCODED_ADDRESS],
    viewAccessPoints: hasPersonPermission?.[VIEW_ACCESS_POINTS] && featureToggle(FEATURES.HZDI_174),
  });

  // HORIZONWEB-12243
  // ML_LAST_ONLY & ML_ALL are deprecated in the app
  // but users may still have them in locale storage
  // temporary conversion to give users the time to update storage using the app
  let storagePersonMobileLocationsRadio = localStorageMapPeople?.[FORM_FIELDS.MOBILE_LOCATION_SCOPE];
  if (storagePersonMobileLocationsRadio === 'ML_LAST_ONLY') {
    storagePersonMobileLocationsRadio = LAST;
  } else if (storagePersonMobileLocationsRadio === 'ML_ALL') {
    storagePersonMobileLocationsRadio = ALL;
  }

  const showPerson = hasViewPerson && hasAnyOfPersonSubPermissions;
  const showCrisisSignal = hasViewPerson && hasPersonPermission?.[VIEW_CRISIS_SIGNAL_LOCATIONS];
  localStorageMapPeople = {
    ...localStorageMapPeople,
    [FORM_FIELDS.PERSON_LOCATION_TYPES]: showPerson || showCrisisSignal ? personGroupData : [],
    [FORM_FIELDS.MOBILE_LOCATION_SCOPE]: showPerson || showCrisisSignal ? storagePersonMobileLocationsRadio : ALL,
    [PERSON_ORGS]: verifiedPeopleOrgs,
    [INCLUDE_SUB_ORGS]: localStorageMapPeople?.[INCLUDE_SUB_ORGS],
    [SEPARATE_MOBILE_LOCATIONS]: hasViewMobileLocationOnMap
      ? localStorageMapPeople?.[SEPARATE_MOBILE_LOCATIONS]
      : undefined,
  };

  return {
    showPerson,
    showCrisisSignal,
    localStorageMapPeople,
  };
}

export function getPersonAdvanceSearchParams(personsFilters) {
  const airline = get(personsFilters, AIRLINE_ID);
  const airport = get(personsFilters, AIRPORT_ID);
  const flightNumberLike = get(personsFilters, FLIGHT_NUMBER_LIKE);
  const addressLabels = get(personsFilters, constants.ADDRESS_LABELS);
  const addressAccuracies = get(personsFilters, constants.ADDRESS_ACCURACY);
  const railwayNameLike = get(personsFilters, RAILWAY);
  const trainNumberLike = get(personsFilters, TRAIN_NUMBER);

  return {
    ...(featureToggle(FEATURES.HZFE_139)
      ? {
          ...parseFilterParams({
            [AIRLINE_IDS]: get(personsFilters, AIRLINE_IDS),
            [FLIGHT_NUMBERS]: get(personsFilters, FLIGHT_NUMBERS),
          }),
          airlineId: undefined,
          flightNumberLike: undefined,
        }
      : {
          airlineId: airline?.id,
          flightNumberLike: flightNumberLike === '' ? undefined : flightNumberLike,
        }),
    airportId: airport?.id,
    addressLabels: addressLabels?.length > 0 ? addressLabels?.map((i) => i?.value) : undefined,
    addressAccuracies: addressAccuracies?.length > 0 ? addressAccuracies?.map((i) => i?.value) : undefined,
    railwayNameLike: railwayNameLike === '' ? undefined : railwayNameLike,
    trainNumberLike: trainNumberLike === '' ? undefined : trainNumberLike,
  };
}
